import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Building2,
  CheckCircle,
  Rocket,
  Users,
  Zap,
  ArrowLeft,
} from 'lucide-react';
import { toast } from 'sonner';
import { betaApi } from '../lib/api';

const PAIN_POINTS = [
  { id: 'lost_leads', label: 'Lost leads' },
  { id: 'sub_coordination', label: 'Sub coordination' },
  { id: 'follow_ups', label: 'Follow-ups' },
  { id: 'estimating_chaos', label: 'Estimating chaos' },
  { id: 'other', label: 'Other' },
];

const EMPLOYEE_OPTIONS = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 employees' },
  { value: '6-10', label: '6-10 employees' },
  { value: '11-25', label: '11-25 employees' },
  { value: '26-50', label: '26-50 employees' },
  { value: '50+', label: '50+ employees' },
];

export default function BetaSignup() {
  const [form, setForm] = useState({
    email: '',
    first_name: '',
    company_name: '',
    phone: '',
    num_employees: '',
    pain_points: [],
    other_pain_point: '',
    zip_code: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [position, setPosition] = useState(null);

  const handlePainPointToggle = (pointId) => {
    setForm(prev => ({
      ...prev,
      pain_points: prev.pain_points.includes(pointId)
        ? prev.pain_points.filter(p => p !== pointId)
        : [...prev.pain_points, pointId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.email || !form.first_name || !form.company_name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await betaApi.signup(form);
      setSubmitted(true);
      setPosition(response.data.position);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg rounded-sm border-slate-700 bg-slate-800 text-white">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="mx-auto h-20 w-20 bg-green-500 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">You&apos;re on the list!</h2>
            {position && (
              <p className="text-orange-400 font-medium mb-4">
                You&apos;re #{position} on the waitlist
              </p>
            )}
            <p className="text-slate-300 mb-6">
              We&apos;ll reach out soon with early access details. Keep an eye on your inbox!
            </p>
            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full rounded-sm bg-orange-500 hover:bg-orange-600">
                  Back to Homepage
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      {/* Header */}
      <header className="border-b border-slate-700 bg-slate-900/50 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 bg-orange-500 rounded-sm flex items-center justify-center">
              <Building2 className="h-6 w-6 text-white" />
            </div>
            <span className="font-bold text-xl text-white">Corbel</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-slate-300 hover:text-white">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Site
            </Button>
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left side - Value props */}
          <div className="text-white">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Rocket className="h-4 w-4" />
              Limited Beta Access
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Join the Corbel Waitlist
            </h1>
            <p className="text-xl text-slate-300 mb-8">
              Get early access + help shape the tool built specifically for residential contractors.
            </p>
            
            <div className="space-y-4">
              {[
                { icon: Zap, text: 'Be the first to try new features' },
                { icon: Users, text: 'Direct input on what we build next' },
                { icon: CheckCircle, text: 'Exclusive beta pricing' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-slate-700 rounded-sm flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-orange-400" />
                  </div>
                  <span className="text-slate-200">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Form */}
          <Card className="rounded-sm border-slate-700 bg-slate-800/50 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-white text-xl">Get Early Access</CardTitle>
              <CardDescription className="text-slate-400">
                Fill out the form below and we&apos;ll be in touch soon.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <Label className="text-slate-200">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    placeholder="you@company.com"
                    className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                </div>

                {/* First Name */}
                <div>
                  <Label className="text-slate-200">First Name *</Label>
                  <Input
                    value={form.first_name}
                    onChange={(e) => setForm({...form, first_name: e.target.value})}
                    placeholder="John"
                    className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                </div>

                {/* Company Name */}
                <div>
                  <Label className="text-slate-200">Company/Business Name *</Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({...form, company_name: e.target.value})}
                    placeholder="Smith Construction"
                    className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    required
                  />
                </div>

                {/* Phone (optional) */}
                <div>
                  <Label className="text-slate-200">Phone Number <span className="text-slate-500">(optional)</span></Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({...form, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                  />
                </div>

                {/* Number of Employees (optional) */}
                <div>
                  <Label className="text-slate-200"># of Employees <span className="text-slate-500">(optional)</span></Label>
                  <Select
                    value={form.num_employees}
                    onValueChange={(val) => setForm({...form, num_employees: val})}
                  >
                    <SelectTrigger className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {EMPLOYEE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Pain Points (optional) */}
                <div>
                  <Label className="text-slate-200 mb-3 block">Biggest Pain Points <span className="text-slate-500">(optional)</span></Label>
                  <div className="space-y-2">
                    {PAIN_POINTS.map(point => (
                      <div key={point.id} className="flex items-center gap-2">
                        <Checkbox
                          id={point.id}
                          checked={form.pain_points.includes(point.id)}
                          onCheckedChange={() => handlePainPointToggle(point.id)}
                          className="border-slate-500"
                        />
                        <label htmlFor={point.id} className="text-slate-300 text-sm cursor-pointer">
                          {point.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {form.pain_points.includes('other') && (
                    <Input
                      value={form.other_pain_point}
                      onChange={(e) => setForm({...form, other_pain_point: e.target.value})}
                      placeholder="Please specify..."
                      className="rounded-sm mt-2 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400"
                    />
                  )}
                </div>

                {/* ZIP (optional) */}
                <div>
                  <Label className="text-slate-200">ZIP Code <span className="text-slate-500">(optional)</span></Label>
                  <Input
                    value={form.zip_code}
                    onChange={(e) => setForm({...form, zip_code: e.target.value})}
                    placeholder="90210"
                    className="rounded-sm mt-1 bg-slate-700 border-slate-600 text-white placeholder:text-slate-400 w-32"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-sm bg-orange-500 hover:bg-orange-600 h-12 text-lg"
                >
                  {submitting ? 'Submitting...' : 'Join the Waitlist'}
                </Button>

                <p className="text-xs text-slate-500 text-center">
                  By signing up, you agree to receive emails about Corbel. Unsubscribe anytime.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
